import {ChangeDetectionStrategy, Component, OnInit, inject} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {Localization} from '../../../../projects/common/src/lib/interface/localization.extension';
import {AuthGuardService} from '../../common/services/auth-guard.service';
import {
  embedAuthorizeUrl, embedDeveloperOf, embedProjectIdOf, embedReasonOf, EmbedUnavailableReason,
} from '../../common/utils/embed-unavailable';

/**
 * What the page says for each reason (string keys), with which icon, and in
 * which tone: 'caution' (yellow) is something the owner can put right,
 * 'error' (red) something that went wrong.
 *
 * The strings may carry <mark> (the word that says what is wrong, highlighted)
 * and <strong> (what to change it to); the template renders them as HTML.
 * A private project also lists the ways to show it (the template).
 */
const MESSAGES: Record<EmbedUnavailableReason, {title: string, text: string, icon: string,
                                                tone?: 'caution' | 'error'}> = {
  private: {title: 'embed_unavailable_private_title', text: 'embed_unavailable_private_text', icon: 'lock', tone: 'caution'},
  not_found: {title: 'embed_unavailable_not_found_title', text: 'embed_unavailable_not_found_text', icon: 'search_off'},
  site: {title: 'embed_unavailable_site_title', text: 'embed_unavailable_site_text', icon: 'public_off'},
  key: {title: 'embed_unavailable_key_title', text: 'embed_unavailable_key_text', icon: 'vpn_key_off'},
  account: {title: 'embed_unavailable_account_title', text: 'embed_unavailable_account_text', icon: 'pause_circle'},
  plan: {title: 'embed_unavailable_plan_title', text: 'embed_unavailable_plan_text', icon: 'workspace_premium', tone: 'caution'},
  error: {title: 'embed_unavailable_error_title', text: 'embed_unavailable_error_text', icon: 'error', tone: 'error'},
  unavailable: {title: 'embed_unavailable_title', text: 'embed_unavailable_text', icon: 'visibility_off'},
};

/**
 * /embed/unavailable?reason=...: a project embedded on someone's website (the
 * iframe API) that cannot be shown, and why.
 *
 * Visitors used to get the bare 403 page or "project not found". Since 8.0.0 a
 * private project no longer opens in an embed at all, and nothing said so --
 * so its owner never learned that sharing it as View only or Public is the fix.
 *
 * The canvas guard shows this without changing the address
 * (CanvasAuthGuardService.embedUnavailable), so a reload tries the embed again.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'app-embed-unavailable-page',
  templateUrl: 'embed-unavailable.page.html',
  styleUrls: ['guest-start.page.scss'],
})
export class EmbedUnavailablePage extends Localization implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authGuard = inject(AuthGuardService);
  private readonly titleService = inject(Title);

  reason: EmbedUnavailableReason = 'unavailable';
  message = MESSAGES.unavailable;
  /**
   * A private project, opened in a new tab: its owner, signed in there, can
   * change who may see it without hunting for it in the dashboard. Anyone
   * else is asked to sign in, and still cannot open it.
   */
  projectUrl = '';
  /**
   * The website's account (the embed's key), as the server named it with the
   * refusal -- by name, never by email address. Its owner authorizes it in
   * one click: their Authorized developers opens in a new tab and asks them
   * to confirm. Handed over in the navigation's state by the canvas guard,
   * never read from the address, so no link can put another name here.
   */
  developerName = '';
  authorizeUrl = '';

  constructor() {
    super();
    const state = this.router.currentNavigation()?.extras?.state;
    const developer = state ? embedDeveloperOf({msg: {developer: state['embedDeveloper']}}) : null;
    const pid = embedProjectIdOf(this.route.snapshot.queryParamMap.get('pid'));
    if (developer && pid) {
      this.developerName = developer.name;
      this.authorizeUrl = embedAuthorizeUrl(window.location.origin, developer.uid, pid);
    }
  }

  /** The name on the button: "this website" for an account with none. */
  get authorizeName(): string {
    return this.developerName || this.strings['embed_unavailable_this_website'];
  }

  ngOnInit() {
    // No auth guard runs on this route, and it is the guard that normally
    // lifts the start-up splash; left up, it hides this page.
    this.authGuard.loader.set(false);
    this.authGuard.loader$.next(false);
    this.reason = embedReasonOf(this.route.snapshot.queryParamMap.get('reason'));
    this.message = MESSAGES[this.reason];
    const pid = embedProjectIdOf(this.route.snapshot.queryParamMap.get('pid'));
    this.projectUrl = this.reason === 'private' && pid ? `${window.location.origin}/app/p/${pid}` : '';
    // The tab title is plain text: without the title's highlight markup.
    this.titleService.setTitle(this.getString(this.message.title).replace(/<[^>]*>/g, ''));
  }

  /** The embed again, from its own address (this page did not change it). */
  tryAgain() {
    window.location.reload();
  }
}
